import { useContext, useEffect, useState } from 'react'
import { Link, withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import {
    CheckboxTableRow,
    ActionTableCell,
    ExTable,
    SelectionFixedFooter,
    TableBody,
    TableHead,
    useMultipleCheckForm,
    TableWrapper,
} from 'components/shared/widget'
import { Extraction, ExtractionSearchForm } from 'entities/extraction'
import { Pagination } from 'components/shared/pagination'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import {
    CreateProtocolSheetForm,
    useCreateProtocolSheetApi,
    useFetchExtractionsApi,
    usePostExtToBioanalyzerApi,
    usePostToDnaApi,
    usePostToMicroarrayApi,
} from 'api/extraction'
import { SearchConditionView } from 'components/shared/search_drawer'
import { DisplaySetting, DisplaySettingModal, TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import { sampleColumnInfoList, SampleTableData } from 'components/sample/sample_table'
import { extractionColumnInfoList, ExtractionTableData } from './extraction_table'
import { Button, Dropdown, Form, Modal } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import qs from 'qs'
import { TranscriptionModal } from 'components/shared/transcription_modal'
import { TranscriptionForm } from 'components/shared/type'
import { BulkEditExtractionForm, EditExtractionForm } from './extraction_form'
import { Flex } from 'components/shared/flex'
import { downloadFile } from 'utils/util'

type Props = {
    history: H.History
}

const ExtractionsPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const params = useQuery<ExtractionSearchForm>()
    const searchForm = useForm<ExtractionSearchForm>({
        page: 1,
        perPage: 50,
        sampleStatus: 'RNA_EXTRACTED',
        ...params,
        isUsed: false,
        ordering: '-sample_priority,sample_deadline_date',
    })
    const api = useFetchExtractionsApi(searchForm)
    const sampleDisplaySetting = useDisplaySetting(sampleColumnInfoList(searchForm), 'Extraction', 'default_hidden')
    const extractionDisplaySetting = useDisplaySetting(extractionColumnInfoList(searchForm), 'Extraction')
    const displaySettings: DisplaySetting[] = [sampleDisplaySetting, extractionDisplaySetting]
    const checkIdForm = useMultipleCheckForm<Extraction>()
    const toDnaApi = usePostToDnaApi()
    const toMicroarrayApi = usePostToMicroarrayApi()
    const toBioanalyzer = usePostExtToBioanalyzerApi()
    const transcriptionForm = useForm<TranscriptionForm>({ items: [] })
    const [showToDnaModal, setShowToDnaModal] = useState<boolean>(false)
    const [showToBioanalyzerModal, setShowToBioanalyzerModal] = useState<boolean>(false)
    const [showToAryModal, setShowToAryModal] = useState<boolean>(false)
    const [editExtraction, setEditExtraction] = useState<Extraction>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const [showBulkEdit, setShowBulkEdit] = useState<boolean>(false)
    const [showProtocol, setShowProtocol] = useState<boolean>(false)
    const createProtocolSheetApi = useCreateProtocolSheetApi()
    const createProtocolSheetForm = useForm<CreateProtocolSheetForm>({ protocol: 'NW-_Norgen' })
    const [search, setSearch] = useState<string>('')

    useEffect(() => {
        api.execute()
    }, [])

    const handleClickRemoveFilter = (key: keyof ExtractionSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffect(() => {
        globalState.setLoading(toDnaApi.loading)
        if (toDnaApi.isSuccess()) {
            props.history.push('transcription_histories?type=DNA')
        }
    }, [toDnaApi.loading])

    useEffect(() => {
        const queryParams = qs.stringify(searchForm.object)
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`extractions?${queryParams}`)
        }
    }, [searchForm.object])

    useEffect(() => {
        transcriptionForm.updateObject(
            'items',
            checkIdForm.form.object.items.map((item) => {
                return { id: item.code, count: 1 }
            })
        )
        createProtocolSheetForm.updateObject(
            'ids',
            checkIdForm.form.object.items.map((item) => `${item.code}`)
        )
    }, [checkIdForm.form.object])

    useEffectSkipFirst(() => {
        globalState.setLoading(createProtocolSheetApi.loading)
        if (createProtocolSheetApi.isSuccess()) {
            setShowProtocol(false)
            const link = `https://docs.google.com/spreadsheets/${createProtocolSheetApi.response.key}`
            window.open(link, '_blank')
            globalState.showConfirm(
                <div>
                    <div>抽出・精製作業シートを作成しました。</div>
                    <div>
                        <a href={link} target="_blank" rel="noreferrer">
                            抽出・精製作業シートへ
                        </a>
                    </div>
                    <div style={{ marginTop: 20 }}>
                        <div>以下の機器の保守管理台帳への記入を忘れずに行ってください</div>
                        {createProtocolSheetApi.response.equipments?.map((equipment) => {
                            return (
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    key={`equipment-${equipment.id}`}
                                    href={`/ledgers/maintenance_ledgers?equipmentId=${equipment.id}&equipmentName=${equipment.name}&equipmentNumber=${equipment.number}&equipmentAlias=${equipment.equipmentAlias}`}
                                >
                                    <div>{`衛生${equipment.equipmentAlias} : ${equipment.name}`}</div>
                                </a>
                            )
                        })}
                    </div>
                </div>,
                () => {},
                { backdropStatic: true }
            )
        }
    }, [createProtocolSheetApi.loading])

    return (
        <>
            <Modal show={showProtocol} onHide={() => setShowProtocol(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>抽出／精製抽出・精製作業シート作成</Modal.Title>
                </Modal.Header>
                <Modal.Body>作成ボタンをクリックで抽出・精製作業シートが作成されます</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowProtocol(false)}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={() => createProtocolSheetApi.execute(createProtocolSheetForm)}>
                        作成
                    </Button>
                </Modal.Footer>
            </Modal>
            <EditExtractionForm
                extraction={editExtraction}
                show={showEdit}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    setShowEdit(false)
                    api.execute()
                }}
            ></EditExtractionForm>
            <BulkEditExtractionForm
                extractions={checkIdForm.form.object.items}
                show={showBulkEdit}
                onClose={() => setShowBulkEdit(false)}
                onComplete={() => {
                    setShowBulkEdit(false)
                    api.execute()
                }}
            />
            <TranscriptionModal
                model="Extraction"
                toModel="cDNA"
                show={showToDnaModal}
                transcriptionForm={transcriptionForm}
                onClickOk={() => {
                    setShowToDnaModal(false)
                    checkIdForm.form.updateObject('items', [])
                    toDnaApi.execute(transcriptionForm)
                }}
                onHide={() => setShowToDnaModal(false)}
            />
            <TranscriptionModal
                model="Extraction"
                toModel="Microarray"
                show={showToAryModal}
                transcriptionForm={transcriptionForm}
                onClickOk={() => {
                    setShowToAryModal(false)
                    checkIdForm.form.updateObject('items', [])
                    toMicroarrayApi.execute(transcriptionForm)
                }}
                onHide={() => setShowToAryModal(false)}
            />
            <TranscriptionModal
                model="Extraction"
                toModel="Bioanalyzer"
                show={showToBioanalyzerModal}
                transcriptionForm={transcriptionForm}
                onClickOk={() => {
                    setShowToBioanalyzerModal(false)
                    checkIdForm.form.updateObject('items', [])
                    toBioanalyzer.execute(transcriptionForm)
                }}
                onHide={() => setShowToBioanalyzerModal(false)}
                hideNote
            />
            <ContentHeader
                headerTitle="Extractions"
                left={
                    <>
                        {/* <CopyTableButton /> */}
                        <SearchConditionView searchForm={searchForm} onClickRemoveButton={handleClickRemoveFilter} hiddenSearchParams={['sampleStatus']} />
                    </>
                }
                right={
                    <>
                        <Form.Group controlId="show-all">
                            <Form.Check
                                type="checkbox"
                                style={{ userSelect: 'none' }}
                                label={'すべて表示'}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        searchForm.update((f) => delete f.sampleStatus)
                                    } else {
                                        searchForm.update((f) => (f.sampleStatus = 'RNA_EXTRACTED'))
                                    }
                                }}
                                checked={!searchForm.object.sampleStatus}
                            />
                        </Form.Group>
                        {/* <Button
                            variant="success"
                            onClick={() => {
                                globalState.showConfirm('CSV出力しますか？', () => {
                                    downloadCsvApi.execute()
                                })
                            }}
                        >
                            CSV
                        </Button> */}
                        <DisplaySettingModal displaySettings={displaySettings} />
                    </>
                }
            />

            <Flex alignItems="center" style={{ marginTop: 10 }} justifyContent="space-between">
                <Flex alignItems="center">
                    <Link to={'/transcription_histories?type=EXTRACTION'}>転記履歴へ</Link>
                    <div style={{ width: 250 }}>
                        <Form.Control
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value)
                            }}
                            placeholder="ExtractionID(部分一致)"
                            onKeyPress={(e) => {
                                if (e.which === 13) {
                                    searchForm.updateObject('extractionIdPartial', search)
                                }
                            }}
                            style={{ marginBottom: 0, width: 250 }}
                        />
                    </div>
                </Flex>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={checkIdForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <CheckboxTableRow head allCheck allItems={api.response.results} multipleCheckForm={checkIdForm}>
                                <ActionTableCell th></ActionTableCell>
                                <TableHeads displaySetting={sampleDisplaySetting} />
                                <TableHeads displaySetting={extractionDisplaySetting} />
                            </CheckboxTableRow>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={sampleDisplaySetting.getColumnLength() + extractionDisplaySetting.getColumnLength() + 2}>
                            {api.response.results.map((extraction: Extraction) => {
                                return (
                                    <CheckboxTableRow key={`extraction-${extraction.id}`} item={extraction} multipleCheckForm={checkIdForm}>
                                        <ActionTableCell
                                            onClick={() => {
                                                setShowEdit(true)
                                                setEditExtraction(extraction)
                                            }}
                                            action="edit"
                                        />
                                        <SampleTableData sample={extraction.sample} displaySetting={sampleDisplaySetting} />
                                        <ExtractionTableData extraction={extraction} displaySetting={extractionDisplaySetting} />
                                    </CheckboxTableRow>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>

            <SelectionFixedFooter
                ids={checkIdForm.ids()}
                onClickReset={() => checkIdForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="extractionIdIn"
                tableName="Extraction"
            >
                <Button variant="primary" onClick={() => setShowBulkEdit(true)}>
                    一括編集
                </Button>

                <Button variant="success" onClick={() => setShowToDnaModal(true)}>
                    cDNAに転記
                </Button>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-success" id="dropdown-basic">
                        ツール
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        {/* <Dropdown.Item
                            onClick={() => {
                                props.history.push(`experiment_sheet?extraction_id_in=${checkIdForm.ids().join(',')}`)
                            }}
                        >
                            実験シート作成
                        </Dropdown.Item> */}

                        <Dropdown.Item
                            onClick={() => {
                                props.history.push(`extractions/picking?extractionIdIn=${checkIdForm.ids().join(',')}`)
                            }}
                        >
                            ピッキングシート印刷
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                setShowProtocol(true)
                            }}
                        >
                            抽出・精製作業シート作成
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                const csv = checkIdForm.form.object.items
                                    .map((extraction) => {
                                        return extraction.code?.split('_').join(',')
                                    })
                                    .join('\n')
                                downloadFile(csv, 'extraction_id_label_print.csv', 'text/csv')
                            }}
                        >
                            ラベル印刷用CSV出力
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </SelectionFixedFooter>
        </>
    )
}

export default withRouter(ExtractionsPage)
